import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { t } from 'i18next';
import { useMemo } from 'react';

import InputAiButton from 'components/desktop/_general/Input/InputExtensions/InputAiButton/InputAiButton';
import InputPlusButton from 'components/desktop/_general/Input/InputExtensions/InputPlusButton/InputPlusButton';
import InputSelect from 'components/desktop/_general/Input/InputExtensions/InputSelect/InputSelect';
import RoundedInput from 'components/desktop/_general/Input/RoundedInput/RoundedInput';
import { SpinnerWhenLoading } from 'components/desktop/_general/Loading/Loading';
import 'components/desktop/_general/UnitWeightEditor/UnitWeightEditor.scss';
import useUnitWeightEditorState from 'components/desktop/_general/UnitWeightEditor/useUnitWeightEditorState';
import { getNumberString } from 'functions/numberToString';
import { Optional } from 'functions/promiseExtensions';
import useCultureCode from 'hooks/redux/useCultureCode';
import * as UnitLight from 'types/Unit/UnitLight';
import * as UnitLightList from 'types/Unit/UnitLightList';
import * as UnitWeight from 'types/UnitWeight/UnitWeight';
import * as UnitWeightList from 'types/UnitWeight/UnitWeightList';

interface IProps {
	ai?: boolean;
	disabled?: boolean;
	getSuggestedUnitWeight?: (selectedUnit: UnitLight.Type) => Promise<Optional<number>>;
	setUnitWeights: (unitWeights: UnitWeightList.Type) => void;
	unitWeights: UnitWeightList.Type;
	units: UnitLightList.Type;
}

const UnitWeightEditor = (props: IProps) => {
	const { state, handlers } = useUnitWeightEditorState();
	const cultureCode = useCultureCode();
	const unitSuggestions = useMemo(
		() => UnitLightList.getUnitSuggestions(props.unitWeights, props.units),
		[props.unitWeights, props.units]
	);

	const getLabel = (): string => {
		return `${t('_general:WEIGHT')} (${t('measurments:GRAMM')}) ${t('_general:PER')} ${t(
			'_general:UNIT'
		)}`;
	};

	const renderTableRow = (
		unitWeight: UnitWeight.Type,
		unitName: string,
		key: number
	): JSX.Element => {
		return (
			<tr key={key} error-key={`UnitWeights[${key}]`} title={UnitWeight.getTitle(unitWeight)}>
				<td>
					<span className="table-unit-weight-editor-span-unit-weight">
						{getNumberString(unitWeight.weight, cultureCode)}
					</span>
					<span className="grey">{unitName}</span>
				</td>
				<td className="grey">{t('_general:PER')}</td>
				<td>{unitWeight.name}</td>
				<td className="table-unit-weight-editor-td-trash-can">
					{!props.disabled && (
						<FontAwesomeIcon
							className="trash-can"
							icon={faTrashCan}
							onClick={() =>
								props.setUnitWeights(
									UnitWeightList.remove(props.unitWeights, unitWeight)
								)
							}
						/>
					)}
				</td>
			</tr>
		);
	};

	return (
		<SpinnerWhenLoading isLoading={state.isLoadingAi}>
			<>
				{!props.disabled && (
					<>
						<label>{getLabel()}</label>
						<div className="input-group" style={{ marginTop: 0 }}>
							<RoundedInput
								disabled={props.disabled || !unitSuggestions.length}
								error-key="WeightAmount"
								handleChange={handlers.setWeight}
								name="weightInput"
								value={state.weight}
							/>
							{props.ai && (
								<InputAiButton
									onClick={() => {
										if (props.getSuggestedUnitWeight) {
											handlers.setIsLoadingAi(true);
											handlers.getSuggestedUnitWeight({
												callback: props.getSuggestedUnitWeight,
												selectedUnit: props.units[state.selectedUnit],
											});
										}
									}}
								/>
							)}
							<InputSelect
								disabled={props.disabled || !unitSuggestions.length}
								handleSelect={handlers.setSelectedUnit}
								value={state.selectedUnit ?? unitSuggestions[0].id ?? ''}
								suggestions={UnitLightList.getUnitSuggestions(
									props.unitWeights,
									props.units
								)}
							/>
							<InputPlusButton
								id="weightInput"
								onPlusClick={() => {
									props.setUnitWeights(
										UnitWeightList.add(
											props.unitWeights,
											unitSuggestions,
											state.selectedUnit,
											state.weight
										)
									);
									handlers.resetValues();
								}}
							/>
						</div>
					</>
				)}
				<div style={{ overflow: 'auto' }}>
					<table
						className="table mb-0"
						style={{ backgroundColor: 'white', overflowX: 'auto' }}
					>
						<tbody>
							{props.unitWeights.map((unitWeight: UnitWeight.Type, i: number) => {
								return renderTableRow(unitWeight, t('measurments:GRAMM'), i);
							})}
						</tbody>
					</table>
					{props.disabled && props.unitWeights.length === 0 && (
						<>
							<label>{t('accessory:NO_UNIT_WEIGHTS_DEFINED')}</label>
						</>
					)}
				</div>
			</>
		</SpinnerWhenLoading>
	);
};

export default UnitWeightEditor;
